import { useCallback, useEffect, useRef, useState } from "react";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { useTelephony } from "../leads/useTelephony";

// Dialer-core — the single-line power-dialer state machine (Phase 1a, WS-6 / D35–D37).
// A self-contained engine the /dialer screen mounts; Phase 2's standalone shell can
// mount the SAME hook. It NEVER builds a parallel list/eligibility engine — it
// iterates the ME campaign call queue (me_campaign_call_queue, S4/D59) filtered by a
// chosen ME campaign or unfiltered (global fallback == today's me_call_queue set),
// reuses the Voice-SDK softphone (useTelephony), and writes wrap-ups through the
// UNCHANGED apply_dial_disposition against the shadow lead (lead_id = source_lead_id
// exposed on the amended view), which routes into the existing automation/cadence engine.
//
// Compliance spine: preview AND power are AGENT-INITIATED. Preview = the agent
// confirms each lead then clicks Dial. Power = the next lead auto-dials after the
// agent's own disposition click (a human action paces every call — single-line,
// non-ATDS). No auto-select-and-fire ahead of the agent (that is deferred
// multi-line). The queue already excludes do_not_call/DNC/terminal/out-of-window
// leads; we additionally enforce the config send window (8am–9pm local) here.

export type DialMode = "preview" | "power";
export type SessionStatus = "idle" | "active" | "paused" | "ended";

export type QueueLead = {
  lead_id: string;
  status: string;
  temperature: string | null;
  next_action_at: string | null;
  call_attempts: number;
  contact_name: string | null;
  address_line1: string | null;
  city: string | null;
  county: string | null;
  best_activity_score: number | null;
  workable_numbers: number;
  best_number: string | null;
};

export type Disposition = {
  id: string;
  code: string;
  label: string;
  color: string | null;
  sort_order: number;
};

export type SessionStats = { dials: number; connects: number; dispositions: number; talk_time_s: number };

const EMPTY_STATS: SessionStats = { dials: 0, connects: 0, dispositions: 0, talk_time_s: 0 };

// Parse "HH:MM" → minutes since midnight.
function hm(v: string): number {
  const [h, m] = v.split(":").map((n) => parseInt(n, 10));
  return (h || 0) * 60 + (m || 0);
}

export function useDialSession() {
  const sb = getSupabaseClient();
  const { call, hangup, callState, error, getCallSid } = useTelephony();

  const [mode, setMode] = useState<DialMode>("preview");
  const [status, setStatus] = useState<SessionStatus>("idle");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [agentUserId, setAgentUserId] = useState<string | null>(null);
  const [dispositions, setDispositions] = useState<Disposition[]>([]);
  const [currentLead, setCurrentLead] = useState<QueueLead | null>(null);
  const [stats, setStats] = useState<SessionStats>(EMPTY_STATS);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [sendWindow, setSendWindow] = useState<{ start: number; end: number }>({ start: 480, end: 1260 }); // 8:00–21:00
  // S4: the ME campaign this session dials (null = global "all raw leads"), + surfaced config.
  const [meCampaignId, setMeCampaignId] = useState<string | null>(null);
  const [agentScriptId, setAgentScriptId] = useState<string | null>(null);

  // Refs mirror mutable session state so async callbacks never read stale closures.
  const workedRef = useRef<Set<string>>(new Set());
  const currentPhoneIdRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const statsRef = useRef<SessionStats>(EMPTY_STATS);
  const modeRef = useRef<DialMode>("preview");
  const connectedRef = useRef(false);
  const talkStartRef = useRef<number | null>(null);
  const meCampaignIdRef = useRef<string | null>(null);       // campaign filter for next()/remaining
  const callOrderRef = useRef<string>("adaptive");           // campaign call_order (ordering)

  useEffect(() => { modeRef.current = mode; }, [mode]);

  // Send window is open when local time is inside [start, end).
  const windowOpen = (() => {
    const now = new Date();
    const mins = now.getHours() * 60 + now.getMinutes();
    return mins >= sendWindow.start && mins < sendWindow.end;
  })();

  // One-time: resolve the acting app user + the disposition set + the send window.
  useEffect(() => {
    let alive = true;
    (async () => {
      const [{ data: uid }, { data: disp }, { data: cfg }] = await Promise.all([
        sb.rpc("current_app_user_id"),
        sb.from("dial_dispositions").select("id,code,label,color,sort_order").eq("is_active", true).order("sort_order"),
        sb.from("config").select("value").eq("key", "sms_send_window").maybeSingle(),
      ]);
      if (!alive) return;
      if (uid) setAgentUserId(uid as unknown as string);
      setDispositions((disp as Disposition[]) ?? []);
      const w = (cfg as { value?: { start?: string; end?: string } } | null)?.value;
      if (w?.start && w?.end) setSendWindow({ start: hm(w.start), end: hm(w.end) });
    })();
    return () => { alive = false; };
  }, [sb]);

  // Track connects + talk time off the softphone call state.
  useEffect(() => {
    if (callState === "open" && !connectedRef.current) {
      connectedRef.current = true;
      talkStartRef.current = Date.now();
      bumpStats({ connects: statsRef.current.connects + 1 });
    }
    if ((callState === "closed" || callState === "idle") && connectedRef.current) {
      connectedRef.current = false;
      if (talkStartRef.current) {
        const secs = Math.round((Date.now() - talkStartRef.current) / 1000);
        bumpStats({ talk_time_s: statsRef.current.talk_time_s + secs });
        talkStartRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callState]);

  const persistStats = useCallback(async (s: SessionStats) => {
    if (!sessionIdRef.current) return;
    await sb.from("dial_sessions").update({ stats: s }).eq("id", sessionIdRef.current);
  }, [sb]);

  const bumpStats = useCallback((patch: Partial<SessionStats>) => {
    const next = { ...statsRef.current, ...patch };
    statsRef.current = next;
    setStats(next);
    void persistStats(next);
  }, [persistStats]);

  const refreshRemaining = useCallback(async () => {
    let q = sb.from("me_campaign_call_queue").select("lead_id", { count: "exact", head: true });
    if (meCampaignIdRef.current) q = q.eq("campaign_id", meCampaignIdRef.current);
    const { count } = await q;
    setRemaining(count ?? null);
  }, [sb]);

  // Pull the next eligible lead — the top of the live queue, skipping leads already
  // worked THIS session (call_attempts increments platform-side, so a worked lead
  // usually leaves the queue on its own; the session-local skip set guarantees
  // forward progress even before that settles). Returns the lead or null.
  const next = useCallback(async (): Promise<QueueLead | null> => {
    // Honor the campaign's call_order: lowest_score_first -> ascending; adaptive /
    // highest_score_first -> descending (the queue's native order). nulls last either way.
    const scoreAsc = callOrderRef.current === "lowest_score_first";
    let q = sb
      .from("me_campaign_call_queue")
      .select("*")
      .order("best_activity_score", { ascending: scoreAsc, nullsFirst: false })
      .order("next_action_at", { ascending: true, nullsFirst: true })
      .limit(1);
    if (meCampaignIdRef.current) q = q.eq("campaign_id", meCampaignIdRef.current);
    const worked = [...workedRef.current];
    if (worked.length) q = q.not("lead_id", "in", `(${worked.join(",")})`);
    const { data } = await q;
    const row = ((data as any[]) ?? [])[0] ?? null;
    // The amended view exposes lead_id (= source_lead_id) + record_status; map to the
    // QueueLead shape the screen expects (status <- record_status) so nothing downstream changes.
    const lead: QueueLead | null = row ? { ...row, status: row.record_status ?? row.status } : null;

    setCurrentLead(lead);
    currentPhoneIdRef.current = null;
    if (lead) {
      // Resolve the dialed number's contact_phones.id (the per-number disposition target).
      const { data: l } = await sb.from("leads").select("primary_contact_id").eq("id", lead.lead_id).maybeSingle();
      const contactId = (l as { primary_contact_id?: string } | null)?.primary_contact_id;
      if (contactId && lead.best_number) {
        const { data: ph } = await sb
          .from("contact_phones").select("id").eq("contact_id", contactId).eq("number_e164", lead.best_number).maybeSingle();
        currentPhoneIdRef.current = (ph as { id?: string } | null)?.id ?? null;
      }
    }
    if (sessionIdRef.current) {
      await sb.from("dial_sessions").update({ current_lead_id: lead?.lead_id ?? null }).eq("id", sessionIdRef.current);
    }
    void refreshRemaining();
    return lead;
  }, [sb, refreshRemaining]);

  // Place the call for a given lead (agent-initiated in BOTH modes).
  const dialLead = useCallback(async (lead: QueueLead | null) => {
    if (!lead?.best_number) return;
    if (!windowOpen) return; // out of the send window — never dial
    workedRef.current.add(lead.lead_id); // it won't be re-pulled by next()
    bumpStats({ dials: statsRef.current.dials + 1 });
    await call(lead.best_number, lead.lead_id, {
      agentUserId: agentUserId ?? undefined,
      sessionId: sessionIdRef.current ?? undefined,
    });
  }, [call, agentUserId, windowOpen, bumpStats]);

  // Preview: the agent clicks Dial for the lead on screen.
  const dial = useCallback(() => dialLead(currentLead), [dialLead, currentLead]);

  const start = useCallback(async (meCampaign?: string | null) => {
    if (!agentUserId) return;
    setBusy(true);
    workedRef.current = new Set();
    statsRef.current = EMPTY_STATS;
    setStats(EMPTY_STATS);
    const campId = meCampaign ?? null;
    meCampaignIdRef.current = campId;
    setMeCampaignId(campId);
    // Honor the campaign's dial-config where cheap + safe: call_order (ordering),
    // calling_hours (gate the send window), agent_script_id (surface the script).
    callOrderRef.current = "adaptive";
    setAgentScriptId(null);
    if (campId) {
      const { data: cfg } = await sb
        .from("campaigns")
        .select("call_order,calling_hours_start,calling_hours_end,agent_script_id")
        .eq("id", campId)
        .maybeSingle();
      const cc = cfg as any;
      if (cc?.call_order) callOrderRef.current = cc.call_order;
      if (cc?.agent_script_id) setAgentScriptId(cc.agent_script_id);
      if (cc?.calling_hours_start && cc?.calling_hours_end) {
        setSendWindow({ start: hm(String(cc.calling_hours_start).slice(0, 5)), end: hm(String(cc.calling_hours_end).slice(0, 5)) });
      }
    }
    const { data, error: insErr } = await sb
      .from("dial_sessions")
      .insert({ agent_id: agentUserId, me_campaign_id: campId, status: "active", stats: EMPTY_STATS })
      .select("id")
      .single();
    if (insErr || !data) { setBusy(false); return; }
    const id = (data as { id: string }).id;
    sessionIdRef.current = id;
    setSessionId(id);
    setStatus("active");
    await next();
    setBusy(false);
  }, [sb, agentUserId, next]);

  // Write the wrap-up (routes into the existing automation/cadence engine) and
  // auto-advance. In power mode the next lead auto-dials after this human action.
  const disposition = useCallback(async (code: string, opts?: { callbackAt?: string }) => {
    if (!currentLead) return;
    setBusy(true);
    hangup();
    await sb.rpc("apply_dial_disposition", {
      p_lead_id: currentLead.lead_id,
      p_code: code,
      p_provider_sid: getCallSid(),
      p_phone_id: currentPhoneIdRef.current,
      p_callback_at: opts?.callbackAt ?? null,
    });
    bumpStats({ dispositions: statsRef.current.dispositions + 1 });
    const nextLead = await next();
    setBusy(false);
    if (modeRef.current === "power" && nextLead && windowOpen) {
      await dialLead(nextLead);
    }
  }, [sb, currentLead, hangup, getCallSid, bumpStats, next, dialLead, windowOpen]);

  const pause = useCallback(async () => {
    hangup();
    setStatus("paused");
    if (sessionIdRef.current) await sb.from("dial_sessions").update({ status: "paused" }).eq("id", sessionIdRef.current);
  }, [sb, hangup]);

  const resume = useCallback(async () => {
    setStatus("active");
    if (sessionIdRef.current) await sb.from("dial_sessions").update({ status: "active" }).eq("id", sessionIdRef.current);
    if (!currentLead) await next();
  }, [sb, currentLead, next]);

  const end = useCallback(async () => {
    hangup();
    setStatus("ended");
    setCurrentLead(null);
    if (sessionIdRef.current) {
      await sb.from("dial_sessions").update({ status: "ended", ended_at: new Date().toISOString() }).eq("id", sessionIdRef.current);
    }
    sessionIdRef.current = null;
    setSessionId(null);
  }, [sb, hangup]);

  return {
    // state
    mode, setMode, status, sessionId, dispositions, currentLead, stats, remaining,
    windowOpen, callState, error, busy, agentReady: !!agentUserId,
    meCampaignId, agentScriptId,
    // actions
    start, dial, disposition, pause, resume, end, hangUp: hangup,
  };
}
